import { useLocation } from "@reach/router";
import { AccountIcon } from "@theme/assets";
import { Link } from "gatsby-plugin-react-i18next";

import {
  DropdownDivider,
  DropdownMenu,
  DropdownMenuItem,
  MsLink,
  ThemeWrapper,
} from "@theme/components";

import { isLinkActive } from "@theme/utils";

import type { NavigationDataProps } from "@theme/types";
import type { ThemeUiProps } from "@theme/types/global";
import type { FC, MouseEvent } from "react";

interface BaseNavDropdownProps extends ThemeUiProps {
  ariaLabel: string;
  className?: string;
  navData: NavigationDataProps;
}

interface LinkNavDropdownProps extends BaseNavDropdownProps {
  type: "link";
  title: string;
}

interface LanguageNavDropdownProps extends BaseNavDropdownProps {
  type: "language";
  searchParams?: string | null;
}

interface AccountNavDropdownProps extends BaseNavDropdownProps {
  type: "account";
  accountLabel: NavigationDataProps["accountLabel"];
  accountLinks: NavigationDataProps["accountLinks"];
  signOut: NavigationDataProps["signOut"];
  pointsModule: JSX.Element | null;
}

type NavDropdownProps = LinkNavDropdownProps | LanguageNavDropdownProps | AccountNavDropdownProps;

export const NavDropdown: FC<NavDropdownProps> = (props) => {
  const { ariaLabel, type, sx, className, navData } = props;
  const { pathname, search } = useLocation();

  const getDropdownContent = () => {
    switch (type) {
      case "link": {
        const dropdownLink = navData.dropdownLinks.find((link) => link.title === props.title);

        if (!dropdownLink) return null;

        return (
          <>
            {dropdownLink.links.map((link) => (
              <DropdownMenuItem key={link.id} {...link} />
            ))}
          </>
        );
      }
      case "language":
        return (
          <>
            {navData.languages.map((lang) => {
              const isActive = lang === navData.currentLanguage;
              // Some areas of the site need to know when the params change.
              // Current reach/router doesn't update when params change so we need a custom solution ¯\_(ツ)_/¯
              const searchParams =
                props.searchParams || props.searchParams === "" ? `?${props.searchParams}` : search;

              const path = `${navData.originalPath}${searchParams || ""}`;

              return (
                // @ts-expect-error Link from gatsby-plugin-react-i18next has some typing issues
                <DropdownMenuItem
                  key={lang}
                  as={Link}
                  label={lang}
                  active={isActive}
                  to={path}
                  language={lang}
                  isNavigation
                  sx={{ textTransform: "uppercase" }}
                />
              );
            })}
          </>
        );
      case "account":
        return (
          <>
            {props.accountLinks?.map(({ to, label }) => (
              <DropdownMenuItem
                key={to}
                as={MsLink}
                variant="override"
                to={to ?? ""}
                label={label}
                active={isLinkActive({ to }, pathname)}
                isNavigation
              />
            ))}
            <DropdownDivider />
            <DropdownMenuItem
              label={props.signOut.label}
              onClick={(event: MouseEvent) => {
                event.preventDefault();
                props.signOut.action();
              }}
            />
          </>
        );
      default:
        return null;
    }
  };

  const buttonLabel = () => {
    switch (type) {
      case "account":
        return (
          <>
            <AccountIcon sx={{ marginLeft: "0 !important", color: "grey300" }} />
            {props.pointsModule}
          </>
        );
      case "link":
        return props.title;
      case "language":
      default:
        return navData.currentLanguage;
    }
  };

  return (
    <DropdownMenu
      buttonLabel={buttonLabel()}
      aria-label={ariaLabel}
      alignment={type === "account" ? "right" : "center"}
      sx={sx}
      className={className}
      openAction="hover"
    >
      <ThemeWrapper theme="LIGHT">{getDropdownContent()}</ThemeWrapper>
    </DropdownMenu>
  );
};
